import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws } from '../lib/awsdb';
import { usePolling } from '../lib/poll';
import { storage } from '../lib/awsStorage';
import { authWrite } from '../lib/authWrite';
import { can } from '../lib/permissions';
import { formatMoney } from '../lib/money';
import { getOutstanding, getDisplayStatus, getDueBucket } from '../lib/invoiceCalc';
import { logActivity } from '../lib/activityLog';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import GeneralInvoiceList from './GeneralInvoiceList';
import GeneralInvoiceDrawer from './GeneralInvoiceDrawer';
import OverflowMenu from '../po/OverflowMenu';
import FilePreviewModal from '../po/FilePreviewModal';
import ArchivePopup from '../po/ArchivePopup';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'application/rtf', 'text/rtf'];
// Some browsers report file.type as '' for RTF. Fall back to extension
// so dragging an .rtf onto the list still opens the extract drawer.
function isExtractable(file) {
  if (!file) return false;
  if (ACCEPTED_MIME.includes(file.type)) return true;
  return /\.(rtf|pdf|png|jpe?g)$/i.test(file.name || '');
}
const STATUS_TABS = [
  { key: 'open', label: 'Open' },
  { key: 'disputed', label: 'Disputed' },
  { key: 'paid', label: 'Paid' },
  { key: 'all', label: 'All' },
];

function logGi(invoiceId, session, payload) {
  return aws.from('invoice_activity_log').insert([{
    invoice_id: invoiceId, actor_code: session.code, actor_name: session.name,
    action_type: payload.action_type, before_value: payload.before_value ?? null,
    after_value: payload.after_value ?? null, notes: payload.notes ?? null
  }]);
}

export default function InvoiceTracker({ session, onSignOut, embedded, catalog = [] }) {
  // Gate the "+ New Invoice" button + the row Action menu (approve /
  // dispute / pay / archive). Read-only users still see the list.
  const canWrite = can(session, 'finance_invoices_write');
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [drawer, setDrawer] = useState(null);
  const [overflow, setOverflow] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const [approveTarget, setApproveTarget] = useState(null);
  const [approveNote, setApproveNote] = useState('');
  const [approveAmount, setApproveAmount] = useState('');
  const [approveFile, setApproveFile] = useState(null);

  const [disputeTarget, setDisputeTarget] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeFile, setDisputeFile] = useState(null);

  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payMethod, setPayMethod] = useState('');
  const [payProof, setPayProof] = useState(null);
  const [payError, setPayError] = useState('');
  const [paySaving, setPaySaving] = useState(false);

  // All invoices from the unified table
  const loadInvoices = useCallback(async () => {
    const { data } = await aws.from('po_invoices').select('*').is('archived_at', null).order('created_at', { ascending: false });
    setInvoices(data || []);
  }, []);
  const loadPayments = useCallback(async () => {
    const { data } = await aws.from('po_invoice_payments').select('id, invoice_id, amount, paid_at, method');
    setPayments(data || []);
  }, []);
  const loadAttachments = useCallback(async () => {
    const { data } = await aws.from('po_invoice_attachments').select('id, invoice_id, storage_path, filename').order('uploaded_at', { ascending: false });
    setAttachments(data || []);
  }, []);
  const loadPayMethods = useCallback(async () => {
    const { data } = await aws.from('payment_methods').select('*').order('name');
    setPaymentMethods(data || []);
  }, []);

  useEffect(() => { loadInvoices(); loadPayments(); loadAttachments(); loadPayMethods(); }, [loadInvoices, loadPayments, loadAttachments, loadPayMethods]);

  usePolling([loadInvoices, loadPayments, loadAttachments]);

  const paidFor = useCallback((id) => payments.filter(p => p.invoice_id === id).reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(inv => {
      if (q) {
        const hit = (inv.invoice_number || '').toLowerCase().includes(q)
          || (inv.supplier_name || '').toLowerCase().includes(q)
          || (inv.po_number_text || '').toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [invoices, search]);

  const kpis = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 7);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const todayStr = today.toISOString().slice(0, 10);
    const thirtyAgo = new Date(today); thirtyAgo.setDate(thirtyAgo.getDate() - 30);
    const thirtyAgoStr = thirtyAgo.toISOString().slice(0, 10);
    const acc = { outstanding: 0, due_week: 0, overdue: 0, disputed: 0, paid_30: 0 };
    invoices.forEach(inv => {
      const paid = paidFor(inv.id);
      const ds = getDisplayStatus(inv, paid);
      const ost = getOutstanding(inv, paid);
      if (ds === 'paid') { if (inv.created_at >= thirtyAgoStr) acc.paid_30 += Number(inv.total || 0); return; }
      if (ds === 'disputed') { acc.disputed += ost; return; }
      if (ds === 'draft') return;
      if (ost <= 0.01) return;
      // Mutually exclusive due bucket
      const bucket = getDueBucket(inv, paid);
      if (bucket === 'overdue') { acc.overdue += ost; }
      else if (bucket === 'due_this_week') { acc.due_week += ost; }
      else { acc.outstanding += ost; }
    });
    return acc;
  }, [invoices, paidFor]);

  const tabCounts = useMemo(() => {
    const c = { open: 0, disputed: 0, paid: 0, all: 0 };
    invoices.forEach(inv => {
      const ds = getDisplayStatus(inv, paidFor(inv.id));
      c.all++;
      if (ds === 'paid') c.paid++;
      else if (ds === 'disputed') c.disputed++;
      else c.open++;
    });
    return c;
  }, [invoices, paidFor]);

  // ── Actions ──
  async function confirmApprove() {
    if (!approveTarget || !approveNote.trim()) return;
    const amt = parseFloat(approveAmount) || 0;
    await authWrite(session, 'po_invoices', 'update', { status: 'approved', amount_approved: amt > 0 ? amt : null }, { column: 'id', value: approveTarget.id });
    let proofPath = null;
    if (approveFile) {
      const key = `invoice/${approveTarget.id}/${Date.now()}-${approveFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error, data: upData } = await storage.from('po-attachments').upload(key, approveFile, { contentType: approveFile.type, upsert: false });
      if (!error) {
        const storedPath = upData?.path || key;
        await authWrite(session, 'po_invoice_attachments', 'insert', {
          invoice_id: approveTarget.id, storage_path: storedPath, filename: approveFile.name,
          mime_type: approveFile.type, size_bytes: approveFile.size, uploaded_by: session.code,
        });
        proofPath = storedPath;
      }
    }
    await logGi(approveTarget.id, session, {
      action_type: 'status_change', before_value: approveTarget.status, after_value: 'approved',
      notes: `detail:${JSON.stringify({ ref: `APR-${approveTarget.id.slice(0,6).toUpperCase()}`, type: 'approval', amount: amt, reason: approveNote.trim(), file: approveFile?.name || null, file_path: proofPath, file_bucket: proofPath ? 'po-attachments' : null })}`
    });
    loadInvoices(); loadAttachments();
    setApproveTarget(null); setApproveNote(''); setApproveAmount(''); setApproveFile(null);
  }

  async function confirmDispute() {
    if (!disputeTarget || !disputeReason.trim()) return;
    await authWrite(session, 'po_invoices', 'update', { status: 'disputed', dispute_reason: disputeReason.trim() }, { column: 'id', value: disputeTarget.id });
    if (disputeFile) {
      const key = `invoice/${disputeTarget.id}/${Date.now()}-${disputeFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error, data: upData } = await storage.from('po-attachments').upload(key, disputeFile, { contentType: disputeFile.type, upsert: false });
      if (!error) {
        const storedPath = upData?.path || key;
        await authWrite(session, 'po_invoice_attachments', 'insert', {
          invoice_id: disputeTarget.id, storage_path: storedPath, filename: disputeFile.name,
          mime_type: disputeFile.type, size_bytes: disputeFile.size, uploaded_by: session.code,
        });
        await logGi(disputeTarget.id, session, { action_type: 'attachment_added', after_value: disputeFile.name, notes: `proof:po-attachments:${storedPath}` });
      }
    }
    await logGi(disputeTarget.id, session, { action_type: 'status_change', before_value: disputeTarget.status, after_value: 'disputed', notes: disputeReason.trim() });
    loadInvoices(); loadAttachments();
    setDisputeTarget(null); setDisputeReason(''); setDisputeFile(null);
  }

  async function confirmPayment() {
    const a = parseFloat(payAmount);
    if (!a || a <= 0) { setPayError('Amount required.'); return; }
    if (!payMethod) { setPayError('Method required.'); return; }
    setPaySaving(true);
    const today = new Date().toISOString().slice(0, 10);
    let inserted;
    try {
      const rows = await authWrite(session, 'po_invoice_payments', 'insert', {
        invoice_id: payTarget.id, paid_at: payDate || today, amount: a,
        method: payMethod, created_by: session.code,
      });
      inserted = Array.isArray(rows) ? rows[0] : null;
    } catch (error) {
      setPayError(error.message); setPaySaving(false); return;
    }
    if (!inserted) { setPayError('No row returned'); setPaySaving(false); return; }
    let proofPath = null;
    if (payProof && inserted) {
      const key = `${inserted.id}/${Date.now()}-${payProof.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error: upErr, data: upData } = await storage.from('payment-proofs').upload(key, payProof, { contentType: payProof.type, upsert: false });
      if (!upErr) {
        proofPath = upData?.path || key;
        await authWrite(session, 'po_invoice_payments', 'update',
          { proof_storage_path: proofPath },
          { column: 'id', value: inserted.id });
      }
    }
    await logGi(payTarget.id, session, {
      action_type: 'payment_logged', after_value: formatMoney(a),
      notes: `detail:${JSON.stringify({ ref: `PAY-${inserted.id.slice(0,6).toUpperCase()}`, amount: a, method: payMethod, date: payDate || today, proof_file: payProof?.name || null, proof_path: proofPath, proof_bucket: proofPath ? 'payment-proofs' : null })}`
    });
    const totalPaid = paidFor(payTarget.id) + a;
    if (getOutstanding({ ...payTarget, amount_approved: payTarget.amount_approved }, totalPaid) <= 0.01 && Number(payTarget.total) > 0) {
      await authWrite(session, 'po_invoices', 'update', { status: 'paid' }, { column: 'id', value: payTarget.id });
      await logGi(payTarget.id, session, { action_type: 'status_change', before_value: payTarget.status, after_value: 'paid' });
    }
    setPaySaving(false); loadInvoices(); loadPayments(); loadAttachments();
    setPayTarget(null); setPayAmount(''); setPayDate(''); setPayMethod(''); setPayProof(null); setPayError('');
  }

  async function simpleStatusChange(inv, next) {
    if (!confirm(`Change ${inv.invoice_number || 'invoice'} to ${next}?`)) return;
    await authWrite(session, 'po_invoices', 'update', { status: next }, { column: 'id', value: inv.id });
    await logGi(inv.id, session, { action_type: 'status_change', before_value: inv.status, after_value: next });
    loadInvoices();
  }

  function deleteInvoice(inv) { setArchiveTarget(inv); }
  async function confirmArchiveInvoice(reason) {
    const inv = archiveTarget; if (!inv) return;
    await authWrite(session, 'po_invoices', 'update', { archived_at: new Date().toISOString(), archived_by: session.name }, { column: 'id', value: inv.id });
    if (reason) await logGi(inv.id, session, { action_type: 'archived', notes: reason });
    setArchiveTarget(null); loadInvoices();
  }

  async function previewFile(att) {
    const bucket = att.storage_path?.startsWith('gi/') ? 'po-attachments' : 'po-attachments';
    const { data, error } = await storage.from(bucket).createSignedUrl(att.storage_path, 3600);
    if (!error && data?.signedUrl) setFilePreview({ url: data.signedUrl, filename: att.filename });
  }

  function openApprove(inv) {
    const ost = getOutstanding(inv, paidFor(inv.id));
    setApproveTarget(inv); setApproveNote(''); setApproveAmount(ost > 0 ? ost.toFixed(2) : ''); setApproveFile(null);
  }
  function openDispute(inv) { setDisputeTarget(inv); setDisputeReason(''); setDisputeFile(null); }
  function openPay(inv) {
    const ost = getOutstanding(inv, paidFor(inv.id));
    setPayTarget(inv); setPayAmount(ost > 0 ? ost.toFixed(2) : ''); setPayDate(new Date().toISOString().slice(0,10)); setPayMethod(''); setPayProof(null); setPayError('');
  }

  function handleDownload() {
    const columns = [
      { label: 'Supplier', width: 22, value: r => r.supplier_name || '' },
      { label: 'Invoice Number', width: 16, value: r => r.invoice_number || '' },
      { label: 'PO Number', width: 14, value: r => r.po_number_text || '' },
      { label: 'Invoice Date', width: 14, value: r => r.invoice_date || r.created_at?.slice(0, 10) || '' },
      { label: 'Due Date', width: 14, value: r => r.due_date || '' },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Total', width: 14, numFmt: '#,##0.00', value: r => Number(r.total || 0) },
      { label: 'Paid', width: 14, numFmt: '#,##0.00', value: r => Number(paidFor(r.id) || 0) },
      { label: 'Outstanding', width: 14, numFmt: '#,##0.00', value: r => Math.max(0, getOutstanding(r, paidFor(r.id))) },
      { label: 'Status', width: 14, value: r => getDisplayStatus(r, paidFor(r.id)) || '' },
    ];
    const filename = `FON_Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Invoices', columns, displayed);
    logExportActivity(session, 'Invoices', filename);
  }

  function handleViewDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && isExtractable(f)) setDrawer({ invoice: null, extractFile: f });
  }

  return (
    <div className={embedded ? '' : 'app-screen'} onDragOver={e => e.preventDefault()} onDrop={handleViewDrop}>
      <div className="main-content">
        <div className="kpi-strip" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          <div className="kpi-tile"><div className="kpi-head"><span className="kpi-label">Outstanding</span></div><div className="kpi-amount">{formatMoney(kpis.outstanding)}</div></div>
          <div className="kpi-tile"><div className="kpi-head"><span className="kpi-label">Due This Week</span></div><div className="kpi-amount">{formatMoney(kpis.due_week)}</div></div>
          <div className="kpi-tile"><div className="kpi-head"><span className="kpi-label">Overdue</span></div><div className="kpi-amount" style={{ color: kpis.overdue > 0 ? 'var(--text-error)' : undefined }}>{formatMoney(kpis.overdue)}</div></div>
          <div className="kpi-tile"><div className="kpi-head"><span className="kpi-label">In Dispute</span></div><div className="kpi-amount">{formatMoney(kpis.disputed)}</div></div>
          <div className="kpi-tile"><div className="kpi-head"><span className="kpi-label">Paid (30d)</span></div><div className="kpi-amount" style={{ color: '#036e43' }}>{formatMoney(kpis.paid_30)}</div></div>
        </div>
        <div className="toolbar">
          <input type="text" className="search-input" placeholder="Search supplier, invoice, PO..." value={search} onChange={e => setSearch(e.target.value)} />
          <div className="toolbar-spacer" />
          {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
          {canWrite && <button className="btn btn-primary" onClick={() => setDrawer({ invoice: null })}>+ New Invoice</button>}
        </div>
        <div className="tab-row">
          {STATUS_TABS.map(t => (
            <button key={t.key} type="button" className={`tab ${statusFilter === t.key ? 'active' : ''}`} onClick={() => setStatusFilter(t.key)}>
              {t.label}<span className="tab-count">{tabCounts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>
        <GeneralInvoiceList invoices={displayed} paidFor={paidFor} statusFilter={statusFilter}
          onOpen={inv => setDrawer({ invoice: inv })} onActions={canWrite ? ((inv, rect) => setOverflow({ invoice: inv, anchorRect: rect })) : undefined}
          attachments={attachments} onPreviewFile={previewFile} />
      </div>

      {drawer && (
        <GeneralInvoiceDrawer session={session} invoice={drawer.invoice} extractFile={drawer.extractFile}
          paidAmount={drawer.invoice ? paidFor(drawer.invoice.id) : 0}
          catalog={catalog} onClose={() => setDrawer(null)} onSaved={() => { loadInvoices(); loadPayments(); loadAttachments(); }} />
      )}

      {overflow && (() => {
        const inv = overflow.invoice;
        const ds = getDisplayStatus(inv, paidFor(inv.id));
        const items = [];
        if (ds !== 'draft' && ds !== 'disputed') items.push({ key: 'pay', label: 'Log Payment', onClick: () => openPay(inv) });
        if (ds === 'draft') {
          items.push({ key: 'approve', label: 'Approve', onClick: () => openApprove(inv) });
          items.push({ key: 'dispute', label: 'Dispute', onClick: () => openDispute(inv) });
        } else if (ds === 'approved') {
          items.push({ key: 'dispute', label: 'Dispute', onClick: () => openDispute(inv) });
          items.push({ key: 'draft', label: 'Back to Draft', onClick: () => simpleStatusChange(inv, 'draft') });
        } else if (ds === 'disputed') {
          items.push({ key: 'resolve', label: 'Resolve', onClick: () => simpleStatusChange(inv, 'draft') });
        } else if (ds === 'paid') {
          items.push({ key: 'reopen', label: 'Reopen', onClick: () => simpleStatusChange(inv, 'approved') });
        }
        items.push({ key: 'view', label: 'View Invoice', onClick: () => setDrawer({ invoice: inv }) });
        items.push({ key: 'delete', label: 'Archive', danger: true, onClick: () => deleteInvoice(inv) });
        return <OverflowMenu anchorRect={overflow.anchorRect} onClose={() => setOverflow(null)} items={items} />;
      })()}

      {approveTarget && createPortal(<>
        <div className="quickpay-backdrop" onClick={() => setApproveTarget(null)} />
        <div className="quickpay-modal" role="dialog">
          <div className="quickpay-title"><span>Approve Invoice</span><span className="quickpay-invoice">{approveTarget.invoice_number}</span></div>
          <div className="quickpay-row"><label>Total Owing</label><div className="readonly-value" style={{ fontSize: 14 }}>{formatMoney(approveTarget.total || 0)}</div></div>
          <div className="quickpay-row"><label>Paid to Date</label><div className="readonly-value" style={{ fontSize: 14, color: '#036e43' }}>{formatMoney(paidFor(approveTarget.id))}</div></div>
          <div className="quickpay-row"><label>Amount Approved</label><input type="number" step="0.01" value={approveAmount} onChange={e => setApproveAmount(e.target.value)} /></div>
          <div className="quickpay-row"><label>Reason<span className="required-star">*</span></label>
            <textarea style={{ width: '100%', minHeight: 60, padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', fontSize: 'var(--text-sm)', resize: 'vertical' }}
              value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Why approved?" autoFocus />
          </div>
          <div className="quickpay-row"><label>Attach file <span className="muted-xs">(optional)</span></label><input type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" onChange={e => setApproveFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} /></div>
          <div className="quickpay-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setApproveTarget(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!approveNote.trim()} onClick={confirmApprove}>Confirm</button>
          </div>
        </div>
      </>, document.body)}

      {disputeTarget && createPortal(<>
        <div className="quickpay-backdrop" onClick={() => setDisputeTarget(null)} />
        <div className="quickpay-modal" role="dialog">
          <div className="quickpay-title"><span>Dispute Invoice</span><span className="quickpay-invoice">{disputeTarget.invoice_number}</span></div>
          <div className="quickpay-row"><label>Reason<span className="required-star">*</span></label>
            <textarea style={{ width: '100%', minHeight: 80, padding: '8px 10px', border: '1px solid var(--border-input)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', fontSize: 'var(--text-sm)', resize: 'vertical' }}
              value={disputeReason} onChange={e => setDisputeReason(e.target.value)} placeholder="Describe the issue..." autoFocus />
          </div>
          <div className="quickpay-row"><label>Evidence <span className="muted-xs">(optional)</span></label>
            <input type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" onChange={e => setDisputeFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
          </div>
          <div className="quickpay-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => { setDisputeTarget(null); setDisputeFile(null); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!disputeReason.trim()} onClick={confirmDispute}>Confirm Dispute</button>
          </div>
        </div>
      </>, document.body)}

      {payTarget && createPortal(<>
        <div className="quickpay-backdrop" onClick={() => setPayTarget(null)} />
        <div className="quickpay-modal" role="dialog">
          <div className="quickpay-title"><span>Log Payment</span><span className="quickpay-invoice">{payTarget.invoice_number}</span></div>
          <div className="quickpay-row"><label>Date</label><input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} /></div>
          <div className="quickpay-row"><label>Amount</label><input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} autoFocus /></div>
          <div className="quickpay-row"><label>Method<span className="required-star">*</span></label>
            <select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
              <option value="">Select method...</option>
              {paymentMethods.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <div className="quickpay-row"><label>Proof <span className="muted-xs">(optional)</span></label><input type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" onChange={e => setPayProof(e.target.files?.[0] || null)} style={{ fontSize: 12 }} /></div>
          {payError && <div className="quickpay-err">{payError}</div>}
          <div className="quickpay-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setPayTarget(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={paySaving} onClick={confirmPayment}>{paySaving ? 'Saving...' : 'Save Payment'}</button>
          </div>
        </div>
      </>, document.body)}

      {filePreview && <FilePreviewModal url={filePreview.url} filename={filePreview.filename} onClose={() => setFilePreview(null)} />}
      {archiveTarget && <ArchivePopup itemName={archiveTarget.invoice_number || 'this invoice'} onClose={() => setArchiveTarget(null)} onConfirm={confirmArchiveInvoice} />}
    </div>
  );
}
